import { useState } from 'react'
import { SlidersHorizontal } from 'lucide-react'
import {
  Button,
  Card,
  DateField,
  SegmentedControl,
  SelectField,
  TextField,
} from '@shared/design-system/ui'
import { useDebounce } from '@shared/lib/use-debounce'
import { useAdminUserSearch } from '../api/admin.api'
import type { AdminLookupUser } from '../api/admin.api'
import {
  dashboardCustomRange,
  dashboardPeriodOptions,
  dashboardToday,
} from '../lib/admin-dashboard-period'
import type { DashboardDateRange, DashboardPeriod } from '../lib/admin-dashboard-period'
import * as styles from './admin-dashboard.css'

export type DashboardSelection = {
  period: DashboardPeriod
  user?: AdminLookupUser
  custom?: DashboardDateRange
}

type AdminDashboardFiltersProps = {
  selection: DashboardSelection
  currency?: string
  currencies?: readonly string[]
  onCurrencyChange?: (currency: string) => void
  onApply: (selection: DashboardSelection) => void
}

export function AdminDashboardFilters(props: AdminDashboardFiltersProps) {
  const { selection } = props
  const key = [
    selection.period,
    selection.user?.id,
    selection.custom?.dateFrom,
    selection.custom?.dateTo,
  ].join('|')
  return <AdminDashboardFilterEditor key={key} {...props} />
}

function AdminDashboardFilterEditor({ selection, onApply }: AdminDashboardFiltersProps) {
  const [expanded, setExpanded] = useState(false)
  const [period, setPeriod] = useState(selection.period)
  const [user, setUser] = useState(selection.user)
  const [query, setQuery] = useState(selection.user?.email ?? '')
  const [from, setFrom] = useState(
    selection.custom?.dateFrom
      ? dashboardToday(new Date(selection.custom.dateFrom))
      : dashboardToday(),
  )
  const [to, setTo] = useState(
    selection.custom?.dateTo
      ? dashboardToday(new Date(Date.parse(selection.custom.dateTo) - 1))
      : dashboardToday(),
  )
  const periodOptions =
    period === '90d' || period === 'all'
      ? [
          ...dashboardPeriodOptions,
          { value: period, label: period === '90d' ? '90 dias' : 'Histórico completo' },
        ]
      : dashboardPeriodOptions
  const debounced = useDebounce(query.trim(), 300)
  const search = useAdminUserSearch(expanded && !user ? debounced : '')
  const settled = query.trim() === debounced
  const custom = dashboardCustomRange(from, to)
  const valid = (!query.trim() || Boolean(user)) && (period !== 'custom' || Boolean(custom))

  return (
    <Card padding="md" className={styles.stack}>
      <div className={styles.toolbar}>
        <SegmentedControl
          value={period}
          options={periodOptions}
          ariaLabel="Período"
          onChange={(next) => {
            setPeriod(next)
            if (next !== 'custom') onApply({ ...selection, period: next })
          }}
        />
        <div className={styles.actions}>
          <Button
            variant="secondary"
            aria-expanded={expanded}
            aria-controls="dashboard-more-filters"
            onClick={() => setExpanded(!expanded)}
          >
            <SlidersHorizontal size={16} />
            Mais filtros{selection.user ? ' (1)' : ''}
          </Button>
        </div>
      </div>
      {period === 'custom' ? (
        <div className={styles.stack}>
          <div className={styles.advanced}>
            <DateField label="De" value={from} onChange={(event) => setFrom(event.target.value)} />
            <DateField label="Até" value={to} onChange={(event) => setTo(event.target.value)} />
          </div>
          <p className={styles.description}>
            Datas inclusivas em Brasília. Máximo de 366 dias.
            {!custom ? ' Informe um intervalo válido.' : ''}
          </p>
        </div>
      ) : null}
      {expanded ? (
        <div id="dashboard-more-filters" className={styles.stack}>
          <div className={styles.advanced}>
            <TextField
              label="Usuário"
              placeholder="Buscar por nome ou e-mail"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value)
                setUser(undefined)
              }}
            />
            <SelectField
              label="Outros períodos"
              value={period === '90d' || period === 'all' ? period : ''}
              options={[
                { value: '', label: 'Selecione' },
                { value: '90d', label: '90 dias' },
                { value: 'all', label: 'Histórico completo' },
              ]}
              onChange={(event) => {
                if (event.target.value) setPeriod(event.target.value as DashboardPeriod)
              }}
            />
          </div>
          {query.trim().length >= 2 && !user ? (
            <div className={styles.stack} aria-live="polite">
              {!settled || search.isFetching ? (
                <span>Buscando usuários...</span>
              ) : search.isError ? (
                <span>Não foi possível buscar. Tente novamente.</span>
              ) : (
                <>
                  {search.data?.items.map((item) => (
                    <Button
                      key={item.id}
                      variant="secondary"
                      onClick={() => {
                        setUser(item)
                        setQuery(item.email)
                      }}
                    >
                      {item.name ? `${item.name} · ` : ''}
                      {item.email}
                    </Button>
                  ))}
                  {search.data?.items.length === 0 ? <span>Nenhum usuário encontrado.</span> : null}
                </>
              )}
              {search.isError ? (
                <Button variant="text" onClick={() => void search.refetch()}>
                  Tentar busca novamente
                </Button>
              ) : null}
            </div>
          ) : null}
          <p className={styles.description}>
            Selecione um usuário para consultar as cobranças vinculadas a ele, em qualquer papel. A
            busca não altera os indicadores.
          </p>
          {period === 'all' ? (
            <p className={styles.partial}>
              O histórico completo pode levar mais tempo. Ele será carregado ao aplicar os filtros.
            </p>
          ) : null}
        </div>
      ) : null}
      {expanded || period === 'custom' ? (
        <div className={styles.actions}>
          <Button
            disabled={!valid}
            onClick={() => {
              onApply({
                period,
                ...(user ? { user } : {}),
                ...(period === 'custom' ? { custom } : {}),
              })
              setExpanded(false)
            }}
          >
            Aplicar filtros
          </Button>
          <Button
            variant="text"
            onClick={() => {
              setQuery('')
              setUser(undefined)
              onApply({ ...selection, user: undefined })
            }}
          >
            Limpar usuário
          </Button>
        </div>
      ) : null}
    </Card>
  )
}
